import { Box, Container, Divider, Grid, IconButton, Typography } from '@material-ui/core'
import { MuiThemeProvider } from '@material-ui/core/styles'
import {
    Add as AddIcon, BackspaceOutlined as BackspaceOutlinedIcon, Print as PrintIcon, Search as SearchIcon
} from '@material-ui/icons'
import EditIcon from '@material-ui/icons/Edit'
import VisibilityIcon from '@material-ui/icons/Visibility'
import Tooltip from '@mui/material/Tooltip'
import MUIDataTable from "mui-datatables"
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import DateField from "../../components/Form/Date"
import SelectSupplier from '../../components/Form/SelectSupplier'
import TextField from '../../components/Form/TextField'
import ButtonUI from '../../components/ui/ButtonUI'
import LoadingUI from '../../components/ui/LoadingUI'
import PageTitle from '../../components/ui/PageTitleUI'
import { useAuth } from '../../hooks/auth'
import { MvmSearch } from '../../interfaces/Mvm'
import MvmStatus from '../../interfaces/MvmStatus'
import MvmApi from '../../services/MvmApi'
import ReportApi from '../../services/ReportApi'
import getDefaultGridOptions, { defaultGridTheme, defaultHeaderOptions } from '../../utils/componentUtils'
import utilsDate from '../../utils/Date'
import permissions from '../../utils/Permissions'
import SelectMvmType from './components/SelectMvmType'
import SelectStatus from './components/SelectStatus'
import { useStyles } from './Style'

interface IFormInput {
    mvmNumber: string;
    suppliers: any;
    invoiceKey: string;
    invoiceNumber: string;
    submitDateStart: Date | null;
    submitDateEnd: Date | null;
    entranceDate: Date | null;
    exitDate: Date | null;
    status: number;
    vehiclePlateNumber: string;
    cartPlateNumber: string;
    mvmType: string;
    containerNumber: string;
    pcaNumber: string;
    sort: string;
    sortDir: string;
}

const Mvm: React.FC = () => {
    const classes = useStyles()
    const [mvms, setMvms] = useState<any>()
    const [loading, setLoading] = useState(false)
    const { user, hasPermission, isPortoReal } = useAuth()
    const { t } = useTranslation("Mvm")
    const { t: _t } = useTranslation("General")
    const { t: tValidation } = useTranslation("Validation")
    const history = useHistory()
    const [page, setPage] = useState(0)
    const [count, setCount] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)


    const { handleSubmit, reset, control, getValues, setValue } = useForm<IFormInput>(
        {
            defaultValues: {
                mvmNumber: '',
                suppliers: [],
                invoiceKey: '',
                invoiceNumber: '',
                submitDateStart: null,
                submitDateEnd: null,
                entranceDate: null,
                exitDate: null,
                status: 0,
                vehiclePlateNumber: '',
                cartPlateNumber: '',
                mvmType: '',
                containerNumber: '',
                pcaNumber: '',
                sortDir: 'desc',
                sort: 'UpdatedAt'
            }
        })
/*
    useEffect(() => {
        const today = new Date();
        setValue('submitDateStart', today);
        setValue('submitDateEnd', today);
    }, [setValue]);
*/
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!hasPermission(permissions.MVM.Add) &&
            !hasPermission(permissions.MVM.Edit) &&
            !hasPermission(permissions.MVM.SuperUser) &&
            !hasPermission(permissions.MVM.Search) &&
            !hasPermission(permissions.MVM.Print))
            history.replace('NoPermission');
    }, [history, user]);

    function print(id: string) {
        ReportApi.printMvm(id);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const data = getValues()
        onSubmit(data)
    }, [history, user, page, rowsPerPage]);

    const onSubmit = (data: any) => {

        setLoading(true)

        //Buscar por fornecedores do usuario Fornecedor
        if (user.isSupplier && data.suppliers?.length === 0)
            data.suppliers = user.suppliers;

        let search: MvmSearch = {
            supplierIds: data.suppliers?.map((element: { code: any; }) => Number(element.code)).join(','),
            mvmNumber: data.mvmNumber,
            invoiceKey: data.invoiceKey,
            invoiceNumber: data.invoiceNumber,
            submitDateStart: data.submitDateStart,
            submitDateEnd: data.submitDateEnd,
            entranceDate: data.entranceDate,
            exitDate: data.exitDate,
            status: data.status?.code,
            vehiclePlateNumber: data.vehiclePlateNumber,
            cartPlateNumber: data.cartPlateNumber,
            mvmType: data.mvmType?.code,
            companyId: Number(user.company.companyID),
            containerNumber: data.containerNumber,
            pcabNumber: data.pcabNumber,
            pageSize: rowsPerPage,
            pageIndex: page,
            sortDir: data.sortDir,
            sort: data.sort
        }

        MvmApi.searchMvmNew(search)
            .then(result => {
                if (result && result.items) {
                    setMvms(result)
                    setCount(result.totalCount)
                    setPage(result.pageIndex)
                }
                else {
                    setMvms([])
                    setCount(0)
                    setPage(0)
                }
            }).catch(() => {
                setMvms([]);
            }).finally(() => {
                setLoading(false);
            })
    }

    const columns = [
        {
            name: 'id',
            display: false,
            options: {
                display: false,
            }
        },
        {
            name: 'status',
            display: false,
            options: {
                display: false,
            }
        },
        {
            name: 'mvmNumber',
            label: String(t('mvmNumber')),
            options: {
                sort: true,
                customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
                    return `${mvms.items[tableMeta.rowIndex].mvmNumber != null ? mvms.items[tableMeta.rowIndex].mvmNumber : ""} ${mvms.items[tableMeta.rowIndex].mviNumber != null ? `(` + mvms.items[tableMeta.rowIndex].mviNumber + `)` : ""}`;
                },
                setCellProps: () => ({
                    align: "left",
                }),
            }
        },
        {
            name: 'submitDate',
            label: String(t('submitDate')),

            options: {
                ...defaultHeaderOptions,
                sort: true,
                customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
                    return utilsDate.DateTimeShort(value);
                }
            }
        },
        {
            name: 'mvmJIS',
            label: String(t('mvmJIS')),
            options: {
                ...defaultHeaderOptions,
                sort: false
            }
        },
        {
            name: 'shippingSupplierId',
            label: String(t('suppliers')),
            options: {
                ...defaultHeaderOptions,
                sort: false,
                customBodyRender: (value: any, tableMeta: any, updateValue: any) => {

                    var suppliers = mvms.items.filter((element: { id: any; }) => element.id === tableMeta.rowData[0])[0]
                        .suppliers.map((element: { supplierCorporateName: any; supplierId: any; }) => element.supplierId + ' - ' + element.supplierCorporateName)

                    return suppliers.join(', ')
                }
            }
        },
        {
            name: 'statusDescription',
            label: String(t('status')),
            options: {
                ...defaultHeaderOptions,
                sort: false
            }
        },
        {
            name: 'vehiclePlateNumber',
            label: String(t('vehiclePlateNumber')),
            options: {
                ...defaultHeaderOptions,
                sort: false
            }
        },
        {
            name: 'cartPlateNumber',
            label: String(t('cartPlateNumber')),
            options: {
                ...defaultHeaderOptions,
                sort: false
            }
        },
        {
            name: 'updatedBy',
            label: String(t('updatedBy')),
            options: {
                ...defaultHeaderOptions,
                sort: true
            }
        },
        {
            name: 'updatedAt',
            label: String(t('updatedAt')),
            options: {
                ...defaultHeaderOptions,
                sort: true,
                customBodyRender: (value: any) => {
                    return utilsDate.DateTimeShort(value);
                }
            }
        },
        {
            name: '',
            label: '',
            options: {
                ...defaultHeaderOptions,
                sort: false,
                customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
                    let canEdit: boolean =
                        (tableMeta.rowData[1] !== MvmStatus.FLAG_STATUS_FINISHED && tableMeta.rowData[1] !== MvmStatus.FLAG_STATUS_CANCELLED) &&
                        (
                            (tableMeta.rowData[1] === MvmStatus.FLAG_STATUS_DRAFT && hasPermission(permissions.MVM.Edit)) ||
                            hasPermission(permissions.MVM.SuperUser)
                        );

                    return (
                        <>
                            <Box display="flex">
                                <a href={canEdit ? "/SubmitMvm/" + tableMeta.rowData[0] : undefined} style={{ cursor: canEdit ? "pointer" : "default" }}>
                                    <Tooltip title={String(_t('edit'))}>
                                        <IconButton disabled={!canEdit} aria-label="Edit" color="primary">
                                            <EditIcon fontSize='small' />
                                        </IconButton>
                                    </Tooltip>
                                </a>
                            </Box>
                        </>
                    );
                }
            }
        },
        {
            name: '',
            label: '',
            options: {
                ...defaultHeaderOptions,
                sort: false,
                customBodyRender: (value: any, tableMeta: any, updateValue: any) => {

                    let canView: boolean = (hasPermission(permissions.MVM.Search) || hasPermission(permissions.MVM.SuperUser));

                    return (
                        <>
                            <Box display="flex">
                                <Tooltip title={String(_t('view'))}>
                                    <a href={canView ? "/SubmitMvm/" + tableMeta.rowData[0] + "/View" : "#"}>
                                        <IconButton disabled={!canView} aria-label="View" color="primary" >
                                            <VisibilityIcon fontSize='small' />
                                        </IconButton>
                                    </a>
                                </Tooltip>
                            </Box>

                        </>
                    );
                }
            }
        },
        {
            name: '',
            label: '',
            options: {
                ...defaultHeaderOptions,
                sort: false,
                customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
                    return (
                        <>
                            <Box display="flex">
                                <Tooltip title={String(_t('print'))}>
                                    <IconButton
                                        disabled={(!hasPermission(permissions.MVM.SuperUser) && !hasPermission(permissions.MVM.Print)) || tableMeta.rowData[1] === MvmStatus.FLAG_STATUS_DRAFT}
                                        aria-label={_t('print')}
                                        color="primary"
                                        onClick={() => {
                                            let idsPrint: any = []
                                            let mvmItem = mvms.items[tableMeta.rowIndex]
                                            idsPrint.push(mvmItem.id)

                                            // Lógica para impressão de mvms de carreta.
                                            if (mvmItem.composition) {
                                                let carts = mvmItem.compositions.map((f: any) => f.cartMvmId)
                                                idsPrint = [...idsPrint, ...carts]
                                            }

                                            for (let i = 0; i < idsPrint.length; i++) {
                                                print(idsPrint[i].toString())
                                            }
                                        }
                                        }
                                    >
                                        <PrintIcon fontSize='small' />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </>
                    );
                }
            }
        }
    ]

    return (
        <Container maxWidth={false} component="main">

            <PageTitle title={t("mvm")} />
            <div className={classes.rootContent}>
                <div className={classes.paper}>

                    <form onSubmit={handleSubmit(onSubmit)} >

                        <Grid container spacing={1}>
                            <Grid item xs={6} style={{ padding: "0px", display: 'flex', alignItems: 'center' }}>
                                <Typography className={classes.root}>
                                    {t("titleSearch")}
                                </Typography>
                            </Grid>

                            <Grid item xs={6} style={{ padding: "0px", textAlign: 'right' }}>
                                <ButtonUI disabled={!hasPermission(permissions.MVM.Add)} color="secondary" onClick={() => { history.push('/SubmitMvm') }} startIcon={<AddIcon />}>{t("add")}</ButtonUI>
                                <ButtonUI onClick={() => reset({ 'mvmType': '' })} startIcon={<BackspaceOutlinedIcon />}>{t("clean")}</ButtonUI>
                                <ButtonUI type="submit" color="primary" startIcon={<SearchIcon />}>{t("search")}</ButtonUI>
                            </Grid>
                        </Grid>

                        <Divider style={{ marginTop: '10px' }} />

                        <Grid container spacing={1}>
                            <Grid item xs={1}>
                                <TextField
                                    label={t("mvmNumber")}
                                    name="mvmNumber"
                                    control={control}
                                    onInput={(e) => {
                                        const input = e.target as HTMLInputElement;
                                        input.value = input.value.replace(/\D/g, '').slice(0, 8);
                                    }}
                                    inputProps={{ maxLength: 8, inputMode: 'numeric', pattern: '[0-9]*' }}
                                    rules={{
                                        validate: (value: string) =>
                                            value === '' || /^\d{8}$/.test(value) || tValidation('invalidFormat')
                                    }}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={2}>
                                <SelectSupplier
                                    name={"suppliers"}
                                    label={t("suppliers")}
                                    control={control}
                                    multiple={true}
                                    style={{ width: '100%' }} />
                            </Grid>

                            <Grid item xs={2}>
                                <TextField
                                    label={t("invoiceKey")}
                                    name="invoiceKey"
                                    control={control}
                                    type="text"
                                    onInput={(e) => { (e.target as HTMLInputElement).value = (e.target as HTMLInputElement).value.slice(0, 44) }}
                                    fullWidth
                                />
                            </Grid>

                            <Grid item xs={1}>
                                <TextField label={t("invoiceNumber")} name="invoiceNumber" control={control} type="number"
                                    onInput={(e) => { (e.target as HTMLInputElement).value = (e.target as HTMLInputElement).value.slice(0, 9) }}
                                    fullWidth />
                            </Grid>

                            <Grid item >
                                <DateField label={t("submitDateStart")} name="submitDateStart" control={control} fullWidth />
                            </Grid>

                            <Grid item >
                                <DateField label={t("submitDateEnd")} name="submitDateEnd" control={control} fullWidth />
                            </Grid>

                            <Grid item >
                                <DateField label={t("entranceDate")} name="entranceDate" control={control} fullWidth />
                            </Grid>

                            <Grid item >
                                <DateField label={t("exitDate")} name="exitDate" control={control} fullWidth />
                            </Grid>

                            <Grid item xs={2}>
                                <SelectStatus
                                    name="status"
                                    label={t('status')}
                                    control={control}
                                    fullWidth />
                            </Grid>

                            <Grid item xs={1}>
                                <TextField
                                    label={t("vehiclePlateNumber")}
                                    name="vehiclePlateNumber"
                                    control={control}
                                    mask="aaa9*99"
                                    fullWidth />
                            </Grid>

                            <Grid item xs={1}>
                                <TextField
                                    label={t("cartPlateNumber")}
                                    name="cartPlateNumber"
                                    control={control}
                                    mask="aaa9*99"
                                    fullWidth />
                            </Grid>

                            <Grid item xs={1}>
                                <SelectMvmType
                                    name="mvmType"
                                    label={t('mvmType')}
                                    control={control}
                                    fullWidth />
                            </Grid>

                            {isPortoReal() &&

                                <Grid item xs={2}>
                                    <TextField
                                        label={t("containerNumber")}
                                        name="containerNumber"
                                        control={control}
                                        fullWidth
                                    />
                                </Grid>
                            }
                            {isPortoReal() &&
                                <Grid item xs={2}>
                                    <TextField
                                        label={t("pcabNumber")}
                                        name="pcabNumber"
                                        control={control}
                                        fullWidth
                                    />
                                </Grid>

                            }
                        </Grid>
                    </form>

                    {
                        loading ? <LoadingUI /> :
                            <MuiThemeProvider theme={defaultGridTheme}>
                                <MUIDataTable
                                    title=""
                                    data={mvms?.items}
                                    columns={columns}

                                    options={{
                                        ...getDefaultGridOptions(_t),
                                        serverSide: true,
                                        pagination: true,
                                        sort: true,
                                        sortOrder: { name: getValues().sort, direction: getValues().sortDir === 'asc' ? 'asc' : 'desc' },
                                        selectToolbarPlacement: "none",
                                        filter: false,
                                        search: false,
                                        print: false,
                                        viewColumns: false,
                                        selectableRows: 'none',
                                        page,
                                        count,
                                        rowsPerPage,
                                        download: true,
                                        downloadOptions: {
                                            filename: t("fileName"),
                                            separator: ';',
                                            filterOptions: {
                                                useDisplayedRowsOnly: true,
                                                useDisplayedColumnsOnly: true,
                                            }
                                        },
                                        onColumnSortChange: (changedColumn: string, direction: string) => {
                                            setValue('sort', changedColumn);
                                            setValue('sortDir', direction);
                                            onSubmit({ ...getValues(), sort: changedColumn, sortDir: direction });
                                        },
                                        onChangePage: (pageIndex) => { setPage(pageIndex) },
                                        onChangeRowsPerPage: (rowsPerPage) => { setPage(0); setRowsPerPage(rowsPerPage) },
                                    }}
                                />
                            </MuiThemeProvider>
                    }
                </div>
            </div>
        </Container>
    )
}
export default Mvm;
